import { useEffect, useMemo } from 'react';
import { useUpdateNodeInternals } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import { GitBranch } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { NodeStruct } from '@/types/node-types';
import {
  CONDITIONAL_EDGE_COLOR,
  SELECTED_CONDITIONAL_EDGE_COLOR,
  getCategoryConfig,
} from '@/constants/node-styling';
import {
  getBranchHandleId,
  getIfConfig,
  getSelectedCase,
  getSwitchConfig,
  hasConditionalEdgeCapabilities,
  ifToSwitchConfig,
  isIfStruct,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  branchTargetNames,
  edgesForBranch,
} from '@/pages/workflow/helper/switch-branch-sync';
import {
  decisionBecauseClause,
  getSwitchDecision,
} from '@/pages/workflow/helper/decision-trace';
import { NodeHandle } from './node-handle';
import { cn } from '@/lib/utils';

interface SwitchBranchRowsProps {
  nodeId: string;
  struct: NodeStruct;
  result: unknown;
  isLocked?: boolean;
}

/**
 * One compact row per Switch branch (each case, then the default), each with
 * its own source handle. Dragging from a row creates a conditional edge
 * labelled with that branch; after a run the taken branch is marked.
 *
 * Inert (renders nothing) unless the backend advertises conditional edges
 * and branch gating, so a stale backend never shows handles that cannot run.
 */
export function SwitchBranchRows({
  nodeId,
  struct,
  result,
  isLocked = false,
}: SwitchBranchRowsProps) {
  const { nodes, edges } = useNodeStore(
    useShallow((state) => ({ nodes: state.nodes, edges: state.edges })),
  );
  const groupedNodes = useNodeStore((state) => state.groupedNodes);
  const getAdvertisedCapabilities = useNodeStore(
    (state) => state.getAdvertisedCapabilities,
  );
  const enabled = useMemo(
    () => hasConditionalEdgeCapabilities(getAdvertisedCapabilities()),
    // groupedNodes is the input the getter memoises on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAdvertisedCapabilities, groupedNodes],
  );

  // --- if/else (Phase 8b): an If shows its two branches in the same rows ---
  const ifConfig = useMemo(
    () => (isIfStruct(struct) ? getIfConfig(struct) : null),
    [struct],
  );
  const config = useMemo(
    () => (ifConfig ? ifToSwitchConfig(ifConfig) : getSwitchConfig(struct)),
    [ifConfig, struct],
  );
  // --- end if/else ---
  const takenLabel = getSelectedCase(result);
  // --- explain every decision (Phase 4): the trace says why, when present ---
  const decision = useMemo(() => getSwitchDecision(result), [result]);
  const because = decision ? decisionBecauseClause(decision) : '';
  // --- end explain ---

  // Handles are dynamic (one per label). React Flow measures handles when
  // the node resizes, not when a handle id changes, so re-measure whenever
  // the set of labels changes or edges with the renamed handle vanish.
  const updateNodeInternals = useUpdateNodeInternals();
  const labelKey = [
    ...config.cases.map((entry) => entry.label),
    config.defaultLabel ?? '',
  ].join('\u0000');
  useEffect(() => {
    if (enabled) updateNodeInternals(nodeId);
  }, [enabled, labelKey, nodeId, updateNodeInternals]);

  if (!enabled) return null;

  const rows: Array<{ label: string; isDefault: boolean }> = ifConfig
    ? [
        // An If always has both branches, even before its condition is set.
        { label: ifConfig.thenLabel, isDefault: false },
        { label: ifConfig.elseLabel, isDefault: true },
      ]
    : [
        ...config.cases.map((entry) => ({
          label: entry.label,
          isDefault: false,
        })),
        ...(config.defaultLabel
          ? [{ label: config.defaultLabel, isDefault: true }]
          : []),
      ];
  if (rows.length === 0) return null;

  return (
    <div className='px-4 pt-1' data-testid='switch-branch-rows'>
      <div className='flex items-center gap-1 mb-1 text-[11px] font-medium text-muted-foreground'>
        <GitBranch className='size-3' />
        <span>Branches</span>
        {takenLabel && (
          <span
            className='ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold text-white'
            style={{ backgroundColor: SELECTED_CONDITIONAL_EDGE_COLOR }}
            data-testid='switch-took-badge'
            title={
              because ? `Took "${takenLabel}" because ${because}` : undefined
            }
          >
            Took {takenLabel}
          </span>
        )}
      </div>
      {Boolean(takenLabel && because) && (
        <div
          className='mb-1 truncate text-[10px] text-amber-900/80 dark:text-amber-200/80'
          title={`Took "${takenLabel}" because ${because}`}
          data-testid='switch-took-because'
        >
          because {because}
        </div>
      )}
      {rows.map(({ label, isDefault }) => {
        const handleId = getBranchHandleId(label);
        const connected = edgesForBranch(edges, nodeId, label).length > 0;
        const targets = branchTargetNames(edges, nodes, nodeId, label);
        const isTaken = takenLabel === label;
        const dimmed = Boolean(takenLabel) && !isTaken;
        const config = getCategoryConfig(struct.node_type, handleId, label, [
          'str',
        ]);
        return (
          <div
            key={handleId}
            className={cn(
              'relative flex items-center gap-2 rounded-lg border px-2 py-1 mb-[6px] text-xs transition-opacity',
              isTaken
                ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'
                : 'border-transparent bg-muted-background',
              dimmed && 'opacity-50',
            )}
            data-testid={`switch-branch-row-${label}`}
            data-branch-label={label}
            data-connected={connected ? 'true' : 'false'}
          >
            <span
              className='size-2 rounded-full shrink-0'
              style={{
                backgroundColor: isTaken
                  ? SELECTED_CONDITIONAL_EDGE_COLOR
                  : CONDITIONAL_EDGE_COLOR,
              }}
            />
            <span
              className='font-medium truncate shrink-0 max-w-[48%]'
              title={label}
            >
              {label}
            </span>
            {ifConfig ? (
              <span className='text-[10px] text-muted-foreground shrink-0'>
                {isDefault ? 'otherwise' : 'if true'}
              </span>
            ) : (
              isDefault && (
                <span className='text-[10px] text-muted-foreground shrink-0'>
                  if nothing matches
                </span>
              )
            )}
            <span
              className='ml-auto min-w-0 text-[10px] text-muted-foreground truncate'
              title={targets.join(', ')}
            >
              {targets.length > 0
                ? `→ ${targets.join(', ')}`
                : 'drag to a node'}
            </span>
            <NodeHandle
              port={{
                id: handleId,
                display_name: ifConfig
                  ? isDefault
                    ? `Otherwise: ${label}`
                    : `Then: ${label}`
                  : isDefault
                    ? `${label} (default branch)`
                    : `Branch: ${label}`,
                info: ifConfig
                  ? isDefault
                    ? 'Runs when the condition is false, or the field is missing.'
                    : 'Runs when the condition is true.'
                  : isDefault
                    ? 'Runs when no case matches.'
                    : 'Runs when this case is the first to match.',
                port_type: { accepted_types: ['str'] },
              }}
              nodeId={nodeId}
              direction='output'
              config={{
                ...config,
                handleStyling: {
                  color: isTaken
                    ? SELECTED_CONDITIONAL_EDGE_COLOR
                    : CONDITIONAL_EDGE_COLOR,
                },
              }}
              isLocked={isLocked}
            />
          </div>
        );
      })}
    </div>
  );
}
